// client/src/coms/components/TopTabs.js
import React, { useEffect, useMemo, useState } from 'react';
import { comsApiGet } from '../comsApi';
import { isMobileViewport } from './comslayout_parts/layoutUtils';

function readVisibleViewportTop() {
  try {
    if (typeof window === 'undefined') return 0;
    const vv = window.visualViewport || null;
    if (!vv) return 0;
    return Math.max(0, Math.round(vv.offsetTop || 0));
  } catch {
    return 0;
  }
}

function getServerLogoUrl(server) {
  if (!server || typeof server !== 'object') return '';
  const v =
    server.logo_url != null
      ? server.logo_url
      : server.logoUrl != null
        ? server.logoUrl
        : server.server_logo_url != null
          ? server.server_logo_url
          : server.serverLogoUrl != null
            ? server.serverLogoUrl
            : '';
  return String(v || '').trim();
}

function makeAcronym(name) {
  const s = String(name || '').trim();
  if (!s) return 'SRV';
  const parts = s.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 4).map((p) => p[0]).join('');
  const a = letters || s.slice(0, 4);
  return String(a).toUpperCase();
}

const TAB_ITEMS = [
  // Phones: recent conversations from everywhere (the Chats button in the bottom bar).
  { id: 'chats', label: 'Chats', icon: '🗨️' },
  { id: 'coms', label: 'Comms', icon: '💬' },
  { id: 'social', label: 'Friends & DMs', icon: '👥' },
  // Watch Together is an Activity now; listed only so an old saved tab can still be named.
  { id: 'watch', label: 'Watch Together', icon: '▶️', sidebarOnly: true },
  { id: 'canvas', label: 'Canvas', icon: '🎨' },
  // Activities (internally "games") opens from the button beside Settings in the user bar. On phones,
  // where that bar sits in the server drawer, the area menu below lists it too (the desktop row does not).
  { id: 'games', label: 'Activities', icon: '🚀' },
  { id: 'studio', label: 'Studio', icon: '🎚️' },
  { id: 'maps', label: 'Maps', icon: '🗺️' },
  { id: 'marketplace', label: 'Marketplace', icon: '🛍️' },
];

function getTabMeta(tabId) {
  return TAB_ITEMS.find((t) => t.id === tabId) || TAB_ITEMS.find((t) => t.id === 'coms');
}

export default function TopTabs({
  server,
  activeTab,
  onSelectTab,
  membersOpen,
  onToggleMembers,
  activeChannel,
  onOpenSidebar,
  onToggleSidebar,
  sidebarButtonIcon,
  sidebarButtonTitle,
  sidebarButtonLabel,
  membersButtonIcon,
  membersButtonTitle,
  membersButtonLabel,
  user,
  setUser,
  // Desktop: extra controls at the right end of the bar (the Layout menu).
  rightSlot = null,
  // Phones: opens the quick conversation switcher.
  onOpenSwitcher,
}) {
  const [isMobile, setIsMobile] = useState(() => isMobileViewport());
  const [logoBroken, setLogoBroken] = useState(false);
  const [fetchedLogoUrl, setFetchedLogoUrl] = useState('');
  const [visibleViewportTop, setVisibleViewportTop] = useState(() => readVisibleViewportTop());
  const [incomingFriendRequestCount, setIncomingFriendRequestCount] = useState(0);
  const [hasUnreadDms, setHasUnreadDms] = useState(false);
  const [mobileTabsOpen, setMobileTabsOpen] = useState(false);

  useEffect(() => {
    function onResize() {
      setIsMobile(isMobileViewport());
      setVisibleViewportTop(readVisibleViewportTop());
    }

    onResize();

    if (typeof window === 'undefined') return undefined;

    const vv = window.visualViewport || null;

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      }
    };
  }, []);

  useEffect(() => {
    if (!isMobile) setMobileTabsOpen(false);
  }, [isMobile]);

  useEffect(() => {
    function onDocPointerDown(e) {
      try {
        const el = e && e.target && e.target.closest ? e.target.closest('[data-coms-mobile-tabs="true"]') : null;
        if (!el) setMobileTabsOpen(false);
      } catch {
        setMobileTabsOpen(false);
      }
    }

    if (!mobileTabsOpen || typeof document === 'undefined') return undefined;
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [mobileTabsOpen]);

  useEffect(() => {
    setLogoBroken(false);
    setFetchedLogoUrl('');
  }, [
    server && server.id,
    server && server.logo_url,
    server && server.logoUrl,
    server && server.server_logo_url,
    server && server.serverLogoUrl,
  ]);

  useEffect(() => {
    let alive = true;

    async function loadLogoFallback() {
      const sid = server && server.id != null ? String(server.id).trim() : '';
      if (!sid) return;

      const direct = getServerLogoUrl(server);
      if (direct) return;

      try {
        const r = await comsApiGet(`/server-logo/${encodeURIComponent(sid)}`);
        if (!alive) return;
        const logo = r && r.logo_url ? String(r.logo_url).trim() : '';
        setFetchedLogoUrl(logo);
      } catch {
        if (!alive) return;
        setFetchedLogoUrl('');
      }
    }

    loadLogoFallback().catch(() => {});
    return () => {
      alive = false;
    };
  }, [server]);

  useEffect(() => {
    let alive = true;
    let timer = null;

    async function loadSocialNotifications() {
      try {
        const [friendReqRes, dmsRes] = await Promise.all([
          comsApiGet('/friend-requests').catch(() => null),
          comsApiGet('/dms').catch(() => null),
        ]);

        if (!alive) return;

        const incoming = Array.isArray(friendReqRes && friendReqRes.incoming) ? friendReqRes.incoming : [];
        const threads = Array.isArray(dmsRes && dmsRes.threads) ? dmsRes.threads : [];
        const unreadDms = threads.some((thread) => !!(thread && thread.has_unread));

        setIncomingFriendRequestCount(incoming.length);
        setHasUnreadDms(unreadDms);
      } catch {
        if (!alive) return;
        setIncomingFriendRequestCount(0);
        setHasUnreadDms(false);
      }
    }

    loadSocialNotifications().catch(() => {});
    timer = setInterval(() => {
      loadSocialNotifications().catch(() => {});
    }, 10000);

    return () => {
      alive = false;
      if (timer) clearInterval(timer);
    };
  }, []);

  const chanLabel = activeChannel
    ? (activeChannel.kind === 'voice' ? `🔊 ${activeChannel.name}` : `# ${activeChannel.name}`)
    : '';
  const chanTopic = activeChannel && activeChannel.kind !== 'voice' ? String(activeChannel.topic || '').trim() : '';

  const serverLogoUrl = useMemo(() => {
    const direct = getServerLogoUrl(server);
    return String(direct || fetchedLogoUrl || '').trim();
  }, [server, fetchedLogoUrl]);

  const hasLogo = !!serverLogoUrl && !logoBroken;
  const serverAcronym = useMemo(() => makeAcronym(server && server.name), [server]);
  const activeTabMeta = useMemo(() => getTabMeta(activeTab), [activeTab]);
  const mobileTopOffset = isMobile ? `calc(${visibleViewportTop}px + env(safe-area-inset-top, 0px))` : '0px';
  const socialHasNotification = incomingFriendRequestCount > 0 || hasUnreadDms;
  const safeSidebarButtonIcon = sidebarButtonIcon || serverAcronym;
  const safeSidebarButtonTitle = sidebarButtonTitle || (server && server.name ? String(server.name) : 'Open server drawer');
  const safeSidebarButtonLabel = sidebarButtonLabel || 'Open server and channel drawer';
  const safeMembersButtonIcon = membersButtonIcon || '👥';
  const safeMembersButtonTitle = membersButtonTitle || 'Open users panel';
  const safeMembersButtonLabel = membersButtonLabel || 'Open users panel';

  if (isMobile) {
    return (
      <div
        className="coms-topbar coms-topbar-mobile"
        data-coms-mobile-tabs="true"
        style={{
          position: 'sticky',
          top: mobileTopOffset,
          zIndex: 20,
          minHeight: 52,
          paddingTop: 0,
          background: 'rgba(var(--vxs-2-rgb, 18,22,32), 0.96)',
          backdropFilter: 'blur(8px)',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <button
          type="button"
          onClick={onToggleSidebar || onOpenSidebar}
          title={safeSidebarButtonTitle}
          aria-label={safeSidebarButtonLabel}
          style={{
            width: 38,
            height: 38,
            borderRadius: 12,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
            flex: '0 0 auto',
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.95 * var(--vx-ov, 1)))',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {sidebarButtonIcon ? (
            <span
              style={{
                position: 'relative',
                zIndex: 1,
                fontSize: 19,
                fontWeight: 950,
                lineHeight: 1,
                textAlign: 'center',
              }}
            >
              {safeSidebarButtonIcon}
            </span>
          ) : hasLogo ? (
            <>
              <img
                src={serverLogoUrl}
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
                onError={() => setLogoBroken(true)}
              />
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.24), rgba(0,0,0,0.05))',
                  pointerEvents: 'none',
                }}
              />
            </>
          ) : (
            <span
              style={{
                position: 'relative',
                zIndex: 1,
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: '0.03em',
                lineHeight: 1,
                padding: '0 4px',
                textAlign: 'center',
              }}
            >
              {serverAcronym}
            </span>
          )}
        </button>

        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <button
            type="button"
            onClick={() => setMobileTabsOpen((v) => !v)}
            aria-label="Open tab drawer"
            aria-expanded={mobileTabsOpen}
            style={{
              width: '100%',
              height: 38,
              borderRadius: 12,
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.055 * var(--vx-ov, 1)))',
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.94 * var(--vx-ov, 1)))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '0 11px',
              cursor: 'pointer',
              position: 'relative',
              minWidth: 0,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>{activeTabMeta.icon}</span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 900,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {activeTabMeta.label}
              </span>
            </span>

            {socialHasNotification ? (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 8,
                  width: 9,
                  height: 9,
                  borderRadius: 999,
                  background: '#ef4444',
                  boxShadow: '0 0 0 2px rgba(var(--vxs-2-rgb, 18,22,32), 0.95)',
                  pointerEvents: 'none',
                }}
              />
            ) : null}

            <span aria-hidden="true" style={{ opacity: 0.75, fontSize: 12, transform: mobileTabsOpen ? 'rotate(180deg)' : 'none' }}>
              ▾
            </span>
          </button>

          {mobileTabsOpen ? (
            <div
              style={{
                position: 'absolute',
                top: 44,
                left: 0,
                right: 0,
                borderRadius: 14,
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                background: 'rgba(var(--vxs-1-rgb, 15,18,27), 0.985)',
                boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
                padding: 6,
                zIndex: 60,
              }}
            >
              {TAB_ITEMS.filter((tab) => !tab.sidebarOnly).map((tab) => {
                const active = activeTab === tab.id;
                const notify = tab.id === 'social' && socialHasNotification;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setMobileTabsOpen(false);
                      onSelectTab(tab.id);
                    }}
                    style={{
                      width: '100%',
                      height: 40,
                      border: 'none',
                      borderRadius: 10,
                      background: active ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.24)' : 'transparent',
                      color: active ? 'var(--vx-text, #fff)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.84 * var(--vx-ov, 1)))',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '0 10px',
                      fontWeight: active ? 900 : 800,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span aria-hidden="true" style={{ width: 20, textAlign: 'center' }}>{tab.icon}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tab.label}</span>
                    {notify ? <Dot /> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        {onOpenSwitcher ? (
          <button
            type="button"
            onClick={onOpenSwitcher}
            title="Switch conversation"
            aria-label="Switch conversation"
            className="vx-topbar-switch"
            data-testid="open-switcher"
          >
            ⇄
          </button>
        ) : null}

        <button
          type="button"
          onClick={onToggleMembers}
          title={safeMembersButtonTitle}
          aria-label={safeMembersButtonLabel}
          aria-pressed={!!membersOpen}
          style={{
            width: 38,
            height: 38,
            borderRadius: 12,
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
            background: membersOpen ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.22)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.92 * var(--vx-ov, 1)))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
            cursor: 'pointer',
            fontSize: 17,
            lineHeight: 1,
          }}
        >
          {safeMembersButtonIcon}
        </button>
      </div>
    );
  }

  return (
    <div
      className="coms-topbar"
      style={{
        position: 'sticky',
        top: mobileTopOffset,
        zIndex: 20,
        paddingTop: 0,
        boxSizing: 'border-box',
      }}
    >
      <div className="coms-topbar-left" style={{ position: 'relative', zIndex: 2 }} />

      <div
        className="coms-topbar-tabs"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          gap: 0,
        }}
      >
        <TabBtn active={activeTab === 'coms'} onClick={() => onSelectTab('coms')}>
          Comms
        </TabBtn>

        <TabBtn active={activeTab === 'social'} onClick={() => onSelectTab('social')} notify={socialHasNotification}>
          Friends &amp; DMs
        </TabBtn>

        <TabBtn active={activeTab === 'canvas'} onClick={() => onSelectTab('canvas')}>
          Canvas
        </TabBtn>

        <TabBtn active={activeTab === 'studio'} onClick={() => onSelectTab('studio')}>
          Studio
        </TabBtn>

        <TabBtn active={activeTab === 'maps'} onClick={() => onSelectTab('maps')}>
          Maps
        </TabBtn>

        <TabBtn active={activeTab === 'marketplace'} onClick={() => onSelectTab('marketplace')}>
          Marketplace
        </TabBtn>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', position: 'relative', zIndex: 2, minWidth: 0 }}>
        {chanLabel ? (
          <div className="coms-topbar-channel" title={chanTopic ? `${chanLabel}\n${chanTopic}` : chanLabel} style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0, maxWidth: '40vw' }}>
            <span style={{ flexShrink: 0 }}>{chanLabel}</span>
            {chanTopic ? (
              <span className="coms-topbar-topic" data-testid="chat-header-topic" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, opacity: 0.68 }}>
                {chanTopic}
              </span>
            ) : null}
          </div>
        ) : null}
        {rightSlot}
      </div>
    </div>
  );
}

function Dot() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 8,
        height: 8,
        borderRadius: 999,
        background: '#ef4444',
        boxShadow: '0 0 0 2px rgba(var(--vxs-2-rgb, 18,22,32), 0.95)',
        pointerEvents: 'none',
        flex: '0 0 auto',
      }}
    />
  );
}

function TabBtn({ active, onClick, children, notify = false }) {
  return (
    <button
      type="button"
      className={`coms-tab ${active ? 'active' : ''}`}
      onClick={onClick}
      style={{ position: 'relative' }}
    >
      {children}
      {notify ? (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 8,
            height: 8,
            borderRadius: 999,
            background: '#ef4444',
            boxShadow: '0 0 0 2px rgba(var(--vxs-2-rgb, 18,22,32), 0.95)',
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </button>
  );
}
